import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import {
  Grid,
  Button,
  Box,
  Typography,
  Autocomplete,
  TextField,
  Skeleton,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  MenuItem,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Container,
  Card,
  CardContent,
  Stack,
  Fade,
  Grow,
  Paper
} from '@mui/material';
import {
  Schedule,
  FilterList,
  Person,
  FitnessCenter,
  CalendarToday,
  AccessTime,
  OpenInNew
} from '@mui/icons-material';
import httpClient from '../httpClient';
import dayjs from 'dayjs';
import logo from '../assets/the_burn_secondary_logo_fullcolor_RGB.png';
import SnowEffect from '../components/SnowEffect';

// Types
interface ScheduleItem {
  id: number;
  instructor_name: string;
  instructor_id: number;
  image_url: string;
  class_time: string;
  class_date: string;
  title: string;
  redirectUrl: string;
}

interface Employee {
  id: number;
  first_name: string;
  last_name: string;
  profile_picture_image_id: number;
}

interface Image {
  id: number;
  sources: Array<{ url: string }>;
}

interface Registrable {
  id: number;
  share_url: string;
}

interface ApiResponse {
  response: {
    data: {
      cache: {
        schedule_items: Array<{
          id: number;
          employee_id: number;
          fs_start_datetime: string;
          title: string;
        }>;
        employees: Employee[];
        images: Image[];
        registrables: Registrable[];
      };
    };
  };
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function getStyles(name: string, classesSelected: string[], theme: any) {
  return {
    fontWeight:
      classesSelected.indexOf(name) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  };
}

interface ClassCardProps {
  item: ScheduleItem;
  handleRedirecting: (item: ScheduleItem) => void;
}

const ClassCard: React.FC<ClassCardProps> = ({ item, handleRedirecting }) => {
  return (
    <Grow in timeout={500}>
      <Card
        elevation={4}
        sx={{
          mb: 2,
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
          },
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={2} md={1}>
              <Box
                component="img"
                sx={{
                  height: 80,
                  width: 80,
                  borderRadius: '50%',
                  objectFit: 'cover',
                  border: '3px solid',
                  borderColor: 'primary.main',
                }}
                alt={`${item.instructor_name} profile`}
                src={item.image_url || logo}
              />
            </Grid>
            
            <Grid item xs={12} sm={10} md={8}>
              <Stack spacing={1}>
                <Typography variant="h6" fontWeight={600} color="primary.main">
                  {dayjs(item.class_time).format('h:mm A')}
                </Typography>
                
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  {item.title}
                </Typography>
                
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                  <Chip
                    icon={<Person />}
                    label={item.instructor_name}
                    variant="outlined"
                    size="small"
                    color="secondary"
                  />
                  <Chip
                    icon={<CalendarToday />}
                    label={dayjs(item.class_time).format('ddd, MMM D')}
                    variant="outlined"
                    size="small"
                    color="primary"
                  />
                  <Chip
                    icon={<AccessTime />}
                    label={dayjs(item.class_time).format('h:mm A')}
                    variant="outlined"
                    size="small"
                    color="info"
                  />
                </Stack>
              </Stack>
            </Grid>
            
            <Grid item xs={12} sm={12} md={3} sx={{ textAlign: { xs: 'center', md: 'right' } }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => handleRedirecting(item)}
                startIcon={<OpenInNew />}
                sx={{
                  px: 3,
                  py: 1.5,
                  borderRadius: 3,
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(0,0,0,0.25)',
                  },
                }}
              >
                Register
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grow>
  );
};

const ClassSchedule: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();

  const updateSearchParams = useCallback((key: string, value: string | string[]) => {
    if (!value || value === '' || (Array.isArray(value) && value.length === 0)) {
      searchParams.delete(key);
    } else {
      searchParams.set(key, Array.isArray(value) ? value.join(',') : value);
    }
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const [scheduleData, setScheduleData] = useState<ScheduleItem[]>([]);
  const [days, setDays] = useState<string[]>([]);
  const [classes, setClasses] = useState<string[]>([]);
  const [classesSelected, setClassesSelected] = useState<string[]>(
    searchParams.get('classesSelected') ? [searchParams.get('classesSelected')!] : []
  );
  const [instructors, setInstructors] = useState<string[]>([]);
  const [instructorSelected, setInstructorSelected] = useState<string | null>(
    searchParams.get('instructorSelected')
  );
  const [daysOut, setDaysOut] = useState<number>(7);
  const [loading, setLoading] = useState<boolean>(true);

  // Update search params when filters change
  useEffect(() => {
    updateSearchParams('classesSelected', classesSelected);
  }, [classesSelected, updateSearchParams]);

  useEffect(() => {
    updateSearchParams('instructorSelected', instructorSelected || '');
  }, [instructorSelected, updateSearchParams]);

  const handleRedirect = (item: ScheduleItem) => {
    if (item.redirectUrl) {
      window.open(item.redirectUrl, '_blank');
    }
  };

  const transformData = useCallback((data: ApiResponse) => {
    if (!data) return;
    
    const temp: ScheduleItem[] = [];
    const dayTemp: string[] = [];
    const tempClasses: string[] = [];
    const tempInstructors: string[] = [];
    
    const oddDataStruct = data.response.data.cache;
    const scheduleItems = oddDataStruct.schedule_items;
    const employees = oddDataStruct.employees;
    const images = oddDataStruct.images;
    const registrables = oddDataStruct.registrables;

    scheduleItems.forEach((item) => {
      const employee = employees.find((emp) => emp.id === item.employee_id);
      const image = images.find((img) => img.id === employee?.profile_picture_image_id);
      
      temp.push({
        id: item.id,
        instructor_name: employee ? `${employee.first_name} ${employee.last_name}` : 'Unknown Instructor',
        instructor_id: employee?.id || 0,
        image_url: image?.sources[0]?.url || '',
        class_time: item.fs_start_datetime,
        class_date: dayjs(item.fs_start_datetime).format('YYYY-MM-DD'),
        title: item.title,
        redirectUrl: registrables.find((reg) => reg.id === item.id)?.share_url || '',
      });

      if (!dayTemp.includes(dayjs(item.fs_start_datetime).format('YYYY-MM-DD'))) {
        dayTemp.push(dayjs(item.fs_start_datetime).format('YYYY-MM-DD'));
      }

      if (!tempClasses.includes(item.title)) {
        tempClasses.push(item.title);
      }

      if (!tempClasses.includes(item.title.slice(0, -3))) {
        tempClasses.push(item.title.slice(0, -3));
      }

      if (searchParams.get('classesSelected') && !tempClasses.includes(searchParams.get('classesSelected')!)) {
        tempClasses.push(searchParams.get('classesSelected')!);
      }

      if (employee && !tempInstructors.includes(`${employee.first_name} ${employee.last_name}`)) {
        tempInstructors.push(`${employee.first_name} ${employee.last_name}`);
      }
    });

    setScheduleData(temp);
    setDays(dayTemp);
    setClasses(tempClasses.sort());
    setInstructors(tempInstructors.sort());
    setLoading(false);
  }, [searchParams]);

  const getGetFitDegreeSchedule = useCallback(async (newDaysOut: number | null = null) => {
    const currentDaysOut = newDaysOut || daysOut;
    const now = dayjs();
    const end = now.add(currentDaysOut, 'day');

    const url = `/schedule/item/app-view/?` +
      `start_datetime=${now.format('YYYY-MM-DD')}%2005%3A00%3A00` +
      `&end_datetime=${end.format('YYYY-MM-DD')}%2004%3A59%3A59` +
      `&__fd_client=widgets&__fd_client_version=2.7.23&fitspot_id=873`;

    try {
      setLoading(true);
      const resp = await httpClient.get<ApiResponse>(url);
      transformData(resp.data);
    } catch (e) {
      console.error('Error fetching schedule:', e);
      setLoading(false);
    }
  }, [daysOut, transformData]);

  const filterData = (): ScheduleItem[] => {
    let temp = scheduleData;
    
    if (classesSelected.length > 0) {
      temp = temp.filter((item) => 
        classesSelected.some((c) => item.title.includes(c))
      );
    }
    
    if (instructorSelected) {
      temp = temp.filter((item) => item.instructor_name === instructorSelected);
    }
    
    return temp;
  };

  const filterDates = (): string[] => {
    const tempData = filterData();
    return days.filter((day) => 
      tempData.some((item) => item.class_date === day)
    );
  };

  useEffect(() => {
    getGetFitDegreeSchedule();
  }, [getGetFitDegreeSchedule]);

  const handleDaysOutChange = (e: SelectChangeEvent<number>) => {
    const value = e.target.value as number;
    setDaysOut(value);
    getGetFitDegreeSchedule(value);
  };

  const handleClassesChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    setClassesSelected(
      typeof value === 'string' ? value.split(',') : value as string[]
    );
  };

  const filteredDates = filterDates();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#E3F2FD', position: 'relative', overflow: 'hidden' }}>
      {/* Snow Effect */}
      <SnowEffect />
      
      {/* Hero Section */}
      <Box sx={{ 
        py: 8, 
        background: `linear-gradient(135deg, #E3F2FD 0%, ${theme.palette.primary.main}15 100%)`,
        textAlign: 'center',
        position: 'relative',
        zIndex: 2
      }}>
        <Container maxWidth="lg">
          <Fade in timeout={1000}>
            <Box>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2
                }}
              >
                Class Schedule
              </Typography>
              <Typography
                variant="h5"
                color="text.secondary"
                sx={{ maxWidth: 600, mx: 'auto' }}
              >
                Discover and book your favorite classes with our comprehensive schedule
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Filters Section */}
      <Container maxWidth="xl" sx={{ py: 4, position: 'relative', zIndex: 2 }}>
        <Fade in timeout={1200}>
          <Box
            sx={{
              p: 4,
              mb: 6,
              borderRadius: 3,
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              background: `linear-gradient(45deg, ${theme.palette.primary.main}40, ${theme.palette.secondary.main}40)`,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
              },
              '& .MuiInputLabel-root': {
                backgroundColor: 'transparent',
              },
            }}
          >
          <Typography
            variant="h5"
            component="h2"
            gutterBottom
            sx={{ 
              mb: 3, 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontWeight: 700,
              color: 'secondary.main',
            }}
          >
            <FilterList sx={{ color: 'secondary.main' }} />
            Filter Classes
          </Typography>

        <Grid container spacing={3}>
          {/* Date Range Filter */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel sx={{ fontWeight: 700 }}>Date Range</InputLabel>
              <Select
                value={daysOut}
                label="Date Range"
                onChange={handleDaysOutChange}
                startAdornment={<Schedule color="action" sx={{ mr: 1 }} />}
              >
                <MenuItem value={7}>Next 7 Days</MenuItem>
                <MenuItem value={14}>Next 14 Days</MenuItem>
                <MenuItem value={30}>Next 30 Days</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Classes Filter */}
          {classes.length > 0 ? (
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <InputLabel sx={{ fontWeight: 700 }}>Class Types</InputLabel>
                <Select
                  multiple
                  value={classesSelected}
                  onChange={handleClassesChange}
                  input={<OutlinedInput label="Class Types" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                  MenuProps={MenuProps}
                  startAdornment={<FitnessCenter color="action" sx={{ mr: 1 }} />}
                >
                  {classes.map((name) => (
                    <MenuItem key={name} value={name} style={getStyles(name, classesSelected, theme)}>
                      <Checkbox checked={classesSelected.indexOf(name) > -1} />
                      <ListItemText primary={name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ) : (
            <Grid item xs={12} sm={6} md={4}>
              <Skeleton variant="rectangular" height={56} />
            </Grid>
          )}

          {/* Instructor Filter */}
          {instructors.length > 0 ? (
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                options={instructors}
                value={instructorSelected}
                onChange={(event, newValue) => setInstructorSelected(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Instructor"
                    InputLabelProps={{ sx: { fontWeight: 700 } }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <Person color="action" sx={{ mr: 1 }} />
                          {params.InputProps.startAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            </Grid>
          ) : (
            <Grid item xs={12} sm={6} md={3}>
              <Skeleton variant="rectangular" height={56} />
            </Grid>
          )}

          {/* Days Out Filter */}
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel sx={{ fontWeight: 700 }}>Days Out</InputLabel>
              <Select
                value={daysOut}
                label="Days Out"
                onChange={handleDaysOutChange}
                startAdornment={<CalendarToday color="action" sx={{ mr: 1 }} />}
              >
                <MenuItem value={7}>7 Days</MenuItem>
                <MenuItem value={14}>14 Days</MenuItem>
                <MenuItem value={30}>30 Days</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
          </Box>
        </Fade>
      </Container>
      
      {/* Schedule Display */}
      <Container maxWidth="xl" sx={{ py: 4, position: 'relative', zIndex: 2 }}>
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={200} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={200} />
        </Box>
      ) : filteredDates.length === 0 ? (
        <Paper
          elevation={2}
          sx={{
            p: 8,
            textAlign: 'center',
            borderRadius: 3,
            bgcolor: 'grey.50',
          }}
        >
          <Typography variant="h5" color="text.secondary" gutterBottom>
            No classes found
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Try adjusting your filters or expanding your date range to see more classes.
          </Typography>
        </Paper>
      ) : (
        <Box>
          {filteredDates.map((day) => (
            <Box key={day} sx={{ mb: 6 }}>
              {/* Day Header */}
              <Box
                sx={{
                  bgcolor: 'primary.main',
                  color: 'white',
                  p: 2,
                  borderRadius: 2,
                  mb: 3,
                  textAlign: 'center',
                }}
              >
                <Typography variant="h4" fontWeight={600}>
                  {dayjs(day).format('dddd, MMMM D')}
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                  {dayjs(day).format('YYYY')}
                </Typography>
              </Box>

              {/* Classes for the day */}
              <Stack spacing={2}>
                {filterData()
                  .filter((item) => item.class_date === day)
                  .sort((a, b) => dayjs(a.class_time).diff(dayjs(b.class_time)))
                  .map((item) => (
                    <ClassCard
                      key={item.id}
                      item={item}
                      handleRedirecting={handleRedirect}
                    />
                  ))}
              </Stack>
            </Box>
          ))}
        </Box>
      )}
    </Container>
    </Box>
  );
};

export default ClassSchedule;
